import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, Package, MapPin, Store, Upload, Download, Settings, Building2, Pencil, Check, X } from "lucide-react";
import api, { API_URL } from "../lib/api";
import { toast } from "sonner";

export default function SystemConfig() {
  const [models, setModels] = useState([]);
  const [storeConfigs, setStoreConfigs] = useState([]);
  const [areas, setAreas] = useState([]);
  const [collectors, setCollectors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newModel, setNewModel] = useState("");
  const [storeForm, setStoreForm] = useState({ store_name: "", area_name: "", assigned_3pa_email: "" });
  const [companyForm, setCompanyForm] = useState({ name: "", contact_email: "", contact_phone: "" });
  const [editingCompany, setEditingCompany] = useState(null); // { id, name, contact_email, contact_phone }
  const [editingModel, setEditingModel] = useState(null); // { oldName, newName }
  const [editingStore, setEditingStore] = useState(null); // { id, store_name, area_name, assigned_3pa_email }
  const [areaForm, setAreaForm] = useState({ area_name: "", assigned_collector: "", sla_days: 5, coverage: "NATIONAL", region: "" });
  const [editingArea, setEditingArea] = useState(null); // { id, area_name, assigned_collector, sla_days, coverage, region }
  const [regions, setRegions] = useState([]);
  const [regionForm, setRegionForm] = useState("");
  const [editingRegion, setEditingRegion] = useState(null); // { id, name }

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [modelsRes, configsRes, areasRes, collectorsRes, companiesRes, regionsRes] = await Promise.all([
        api.get("/models"),
        api.get("/stores-config"),
        api.get("/areas"),
        api.get("/collectors"),
        api.get("/companies"),
        api.get("/regions"),
      ]);
      setModels(modelsRes.data || []);
      setStoreConfigs(configsRes.data || []);
      setAreas(areasRes.data || []);
      setCollectors(collectorsRes.data || []);
      setCompanies(companiesRes.data || []);
      setRegions(regionsRes.data || []);
    } catch { toast.error("Failed to load config data"); }
    setLoading(false);
  };

  const addModel = async () => {
    if (!newModel.trim()) return;
    try {
      await api.post("/models/create", { name: newModel.trim() });
      setNewModel("");
      toast.success("Model added");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add model");
    }
  };

  const deleteModel = async (name) => {
    if (!window.confirm(`Delete model "${name}"?`)) return;
    try {
      await api.delete(`/models`, { params: { name } });
      toast.success("Model deleted");
      loadAll();
    } catch { toast.error("Failed to delete"); }
  };

  const saveModelRename = async () => {
    if (!editingModel?.newName?.trim()) return;
    try {
      await api.put(`/models`, { name: editingModel.newName.trim() }, { params: { name: editingModel.oldName } });
      setEditingModel(null);
      toast.success("Model renamed");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to rename");
    }
  };

  const addStoreConfig = async () => {
    if (!storeForm.store_name || !storeForm.area_name || !storeForm.assigned_3pa_email) {
      toast.error("Fill all fields");
      return;
    }
    try {
      await api.post("/stores-config", storeForm);
      setStoreForm({ store_name: "", area_name: "", assigned_3pa_email: "" });
      toast.success("Store config added");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    }
  };

  const handleBulkUpload = async (endpoint, fileInputId) => {
    const input = document.getElementById(fileInputId);
    if (!input?.files?.[0]) return;
    const formData = new FormData();
    formData.append("file", input.files[0]);
    try {
      const { data } = await api.post(endpoint, formData);
      toast.success(data.message);
      input.value = "";
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
      input.value = "";
    }
  };

  const downloadSample = async (endpoint, filename) => {
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_URL}/api${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
  };

  const deleteStoreConfig = async (id) => {
    if (!window.confirm("Delete this store configuration?")) return;
    try {
      await api.delete(`/stores-config/${id}`);
      toast.success("Config deleted");
      loadAll();
    } catch { toast.error("Failed to delete"); }
  };

  const saveStoreEdit = async () => {
    if (!editingStore?.store_name?.trim()) return;
    try {
      await api.put(`/stores-config/${editingStore.id}`, {
        store_name: editingStore.store_name.trim(),
        area_name: editingStore.area_name,
        assigned_3pa_email: editingStore.assigned_3pa_email,
      });
      setEditingStore(null);
      toast.success("Store updated");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  // ===== Areas CRUD =====
  const addArea = async () => {
    if (!areaForm.area_name.trim() || !areaForm.assigned_collector) {
      toast.error("Area name and collector required");
      return;
    }
    const sla = parseInt(areaForm.sla_days, 10);
    if (!sla || sla < 1 || sla > 60) {
      toast.error("SLA must be between 1 and 60 working days");
      return;
    }
    try {
      await api.post("/areas", { ...areaForm, sla_days: sla, region: areaForm.region || null });
      setAreaForm({ area_name: "", assigned_collector: "", sla_days: 5, coverage: "NATIONAL", region: "" });
      toast.success("Area added");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add area");
    }
  };

  const saveAreaEdit = async () => {
    if (!editingArea?.area_name?.trim() || !editingArea?.assigned_collector) return;
    const sla = parseInt(editingArea.sla_days, 10);
    if (!sla || sla < 1 || sla > 60) {
      toast.error("SLA must be between 1 and 60 working days");
      return;
    }
    try {
      await api.put(`/areas/${editingArea.id}`, {
        area_name: editingArea.area_name.trim(),
        assigned_collector: editingArea.assigned_collector,
        sla_days: sla,
        coverage: editingArea.coverage || "NATIONAL",
        region: editingArea.region || "",
      });
      setEditingArea(null);
      toast.success("Area updated");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update area");
    }
  };

  const deleteArea = async (area) => {
    if (!window.confirm(`Delete area "${area.area_name}"?`)) return;
    try {
      await api.delete(`/areas/${area.id}`);
      toast.success("Area deleted");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete area");
    }
  };

  // ===== Regions CRUD =====
  const addRegion = async () => {
    const name = regionForm.trim();
    if (!name) return;
    try {
      await api.post("/regions", { name });
      setRegionForm("");
      toast.success("Region added");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add region");
    }
  };

  const saveRegionEdit = async () => {
    if (!editingRegion?.name?.trim()) return;
    try {
      await api.put(`/regions/${editingRegion.id}`, { name: editingRegion.name.trim() });
      setEditingRegion(null);
      toast.success("Region updated");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update region");
    }
  };

  const deleteRegion = async (region) => {
    if (region.in_use > 0) {
      toast.error(`Cannot delete — ${region.in_use} area(s) still use ${region.name}. Reassign them first.`);
      return;
    }
    if (!window.confirm(`Delete region "${region.name}"?`)) return;
    try {
      await api.delete(`/regions/${region.id}`);
      toast.success("Region deleted");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete region");
    }
  };

  // ===== Companies =====
  const TYPE_META = {
    SKYWORTH: { label: "Skyworth", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    CARGODASH: { label: "CargoDash", cls: "bg-teal-50 text-teal-700 border-teal-200" },
    THIRD_PARTY: { label: "3rd Party", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  };

  const addCompany = async () => {
    if (!companyForm.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    try {
      await api.post("/companies", { ...companyForm, type: "THIRD_PARTY" });
      setCompanyForm({ name: "", contact_email: "", contact_phone: "" });
      toast.success("Company added");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add company");
    }
  };

  const saveCompanyEdit = async () => {
    if (!editingCompany?.name?.trim()) return;
    try {
      const payload = {
        contact_email: editingCompany.contact_email || "",
        contact_phone: editingCompany.contact_phone || "",
      };
      // Only send name when company is NOT built-in (backend blocks rename on built-ins)
      if (!editingCompany.is_builtin) {
        payload.name = editingCompany.name;
      }
      await api.put(`/companies/${editingCompany.id}`, payload);
      setEditingCompany(null);
      toast.success("Company updated");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const deleteCompany = async (company) => {
    if (company.is_builtin) {
      toast.error(`${company.name} is a built-in company and cannot be deleted`);
      return;
    }
    const warn = company.user_count > 0
      ? `"${company.name}" has ${company.user_count} user(s) linked. Deleting will unassign them. Continue?`
      : `Delete "${company.name}"?`;
    if (!window.confirm(warn)) return;
    try {
      const { data } = await api.delete(`/companies/${company.id}`);
      const cleared = (data.users_unassigned || 0) + (data.job_cards_unassigned || 0);
      toast.success(cleared > 0
        ? `Company deleted — ${data.users_unassigned || 0} user(s) and ${data.job_cards_unassigned || 0} job card(s) unassigned`
        : "Company deleted");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) return <div className="text-slate-500 p-8">Loading...</div>;

  return (
    <div data-testid="system-config-page" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 flex items-center gap-2">
          <Settings size={24} className="text-teal-600" /> System Configuration
        </h1>
        <p className="text-slate-500 text-sm mt-1">Manage models, stores, areas and 3PA assignments</p>
      </div>

      <Tabs defaultValue="models" className="w-full">
        <TabsList className="bg-slate-100 border border-slate-200">
          <TabsTrigger data-testid="tab-models" value="models" className="data-[state=active]:bg-white">
            <Package size={14} className="mr-2" /> Models
          </TabsTrigger>
          <TabsTrigger data-testid="tab-stores" value="stores" className="data-[state=active]:bg-white">
            <Store size={14} className="mr-2" /> Stores / Areas / 3PA
          </TabsTrigger>
          <TabsTrigger data-testid="tab-areas" value="areas" className="data-[state=active]:bg-white">
            <MapPin size={14} className="mr-2" /> Areas
          </TabsTrigger>
          <TabsTrigger data-testid="tab-regions" value="regions" className="data-[state=active]:bg-white">
            <MapPin size={14} className="mr-2" /> Regions
          </TabsTrigger>
          <TabsTrigger data-testid="tab-companies" value="companies" className="data-[state=active]:bg-white">
            <Building2 size={14} className="mr-2" /> Companies
          </TabsTrigger>
        </TabsList>

        {/* Models Tab */}
        <TabsContent value="models" className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2 items-end">
            <Input
              data-testid="new-model-input"
              value={newModel}
              onChange={(e) => setNewModel(e.target.value)}
              placeholder="Enter new model name..."
              className="bg-white border-slate-300 max-w-sm"
              onKeyDown={(e) => e.key === "Enter" && addModel()}
            />
            <Button
              data-testid="add-model-btn"
              onClick={addModel}
              disabled={!newModel.trim()}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Plus size={14} className="mr-1" /> Add Model
            </Button>
            <div className="border-l border-slate-200 h-8 mx-1" />
            <input id="models-upload" type="file" accept=".xlsx,.xls" className="hidden"
              onChange={() => handleBulkUpload("/models/bulk-upload", "models-upload")} />
            <Button
              data-testid="bulk-upload-models-btn"
              onClick={() => document.getElementById("models-upload").click()}
              variant="outline"
              className="border-slate-300 text-slate-600"
            >
              <Upload size={14} className="mr-1" /> Import Excel
            </Button>
            <Button
              data-testid="sample-models-btn"
              onClick={() => downloadSample("/models/sample-file", "models_import_sample.xlsx")}
              variant="ghost"
              className="text-slate-500"
            >
              <Download size={14} className="mr-1" /> Sample File
            </Button>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                  <th className="text-left p-3">Model Name</th>
                  <th className="text-right p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {models.length === 0 && (
                  <tr><td colSpan={2} className="p-8 text-center text-slate-400">No models configured</td></tr>
                )}
                {models.map((m, i) => {
                  const name = typeof m === "string" ? m : m.name || m;
                  const isEditing = editingModel?.oldName === name;
                  return (
                    <tr key={i} data-testid={`model-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 text-slate-800 font-medium">
                        {isEditing ? (
                          <Input
                            data-testid={`edit-model-input-${i}`}
                            value={editingModel.newName}
                            onChange={(e) => setEditingModel({ ...editingModel, newName: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") saveModelRename(); if (e.key === "Escape") setEditingModel(null); }}
                            autoFocus
                            className="h-8 bg-white border-slate-300 text-sm max-w-sm"
                          />
                        ) : (
                          name
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {isEditing ? (
                          <div className="flex justify-end gap-1">
                            <Button data-testid={`save-model-${i}`} variant="ghost" size="sm" onClick={saveModelRename} className="text-teal-600 hover:text-teal-700 hover:bg-teal-50">
                              <Check size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingModel(null)} className="text-slate-400 hover:text-slate-600">
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1">
                            <Button
                              data-testid={`edit-model-${i}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingModel({ oldName: name, newName: name })}
                              className="text-slate-400 hover:text-teal-600 hover:bg-teal-50"
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              data-testid={`delete-model-${i}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteModel(name)}
                              className="text-red-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Stores / Areas / 3PA Tab */}
        <TabsContent value="stores" className="mt-4 space-y-4">
          <div className="flex gap-2 mb-3">
            <input id="stores-upload" type="file" accept=".xlsx,.xls" className="hidden"
              onChange={() => handleBulkUpload("/stores-config/bulk-upload", "stores-upload")} />
            <Button
              data-testid="bulk-upload-stores-btn"
              onClick={() => document.getElementById("stores-upload").click()}
              variant="outline"
              className="border-slate-300 text-slate-600"
            >
              <Upload size={14} className="mr-1" /> Import Excel
            </Button>
            <Button
              data-testid="sample-stores-btn"
              onClick={() => downloadSample("/stores-config/sample-file", "store_config_import_sample.xlsx")}
              variant="ghost"
              className="text-slate-500"
            >
              <Download size={14} className="mr-1" /> Sample File
            </Button>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Add New Store Configuration</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Store Name</Label>
                <Input
                  data-testid="store-name-input"
                  value={storeForm.store_name}
                  onChange={(e) => setStoreForm({ ...storeForm, store_name: e.target.value })}
                  placeholder="Store name"
                  className="bg-white border-slate-300"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Area / Location</Label>
                <Select value={storeForm.area_name} onValueChange={(v) => setStoreForm({ ...storeForm, area_name: v })}>
                  <SelectTrigger data-testid="store-area-select" className="bg-white border-slate-300">
                    <SelectValue placeholder="Select area" />
                  </SelectTrigger>
                  <SelectContent>
                    {areas.map((a) => (
                      <SelectItem key={a.area_name} value={a.area_name}>{a.area_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Assigned 3rd Party</Label>
                <Select value={storeForm.assigned_3pa_email} onValueChange={(v) => setStoreForm({ ...storeForm, assigned_3pa_email: v })}>
                  <SelectTrigger data-testid="store-3pa-select" className="bg-white border-slate-300">
                    <SelectValue placeholder="Select 3PA" />
                  </SelectTrigger>
                  <SelectContent>
                    {collectors.map((c) => (
                      <SelectItem key={c.email} value={c.email}>{c.name} ({c.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              data-testid="add-store-config-btn"
              onClick={addStoreConfig}
              disabled={!storeForm.store_name || !storeForm.area_name || !storeForm.assigned_3pa_email}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Plus size={14} className="mr-1" /> Add Configuration
            </Button>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                  <th className="text-left p-3">Store</th>
                  <th className="text-left p-3">Area / Location</th>
                  <th className="text-left p-3">Assigned 3rd Party</th>
                  <th className="text-right p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {storeConfigs.length === 0 && (
                  <tr><td colSpan={4} className="p-8 text-center text-slate-400">No store configurations</td></tr>
                )}
                {storeConfigs.map((sc) => {
                  const isEditing = editingStore?.id === sc.id;
                  return (
                    <tr key={sc.id} data-testid={`store-config-${sc.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 font-medium text-slate-800">
                        {isEditing ? (
                          <Input
                            data-testid={`edit-store-name-${sc.id}`}
                            value={editingStore.store_name}
                            onChange={(e) => setEditingStore({ ...editingStore, store_name: e.target.value })}
                            className="h-8 bg-white border-slate-300 text-sm max-w-[220px]"
                          />
                        ) : (
                          sc.store_name
                        )}
                      </td>
                      <td className="p-3">
                        {isEditing ? (
                          <Select value={editingStore.area_name} onValueChange={(v) => setEditingStore({ ...editingStore, area_name: v })}>
                            <SelectTrigger className="h-8 bg-white border-slate-300 text-xs w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {areas.map((a) => (<SelectItem key={a.area_name} value={a.area_name}>{a.area_name}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <MapPin size={12} className="text-teal-600" />
                            <span className="text-slate-700">{sc.area_name}</span>
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        {isEditing ? (
                          <Select value={editingStore.assigned_3pa_email} onValueChange={(v) => setEditingStore({ ...editingStore, assigned_3pa_email: v })}>
                            <SelectTrigger className="h-8 bg-white border-slate-300 text-xs w-56"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {collectors.map((c) => (<SelectItem key={c.email} value={c.email}>{c.name} ({c.email})</SelectItem>))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                            {sc.assigned_3pa_name || sc.assigned_3pa_email}
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {isEditing ? (
                          <div className="flex justify-end gap-1">
                            <Button data-testid={`save-store-${sc.id}`} variant="ghost" size="sm" onClick={saveStoreEdit} className="text-teal-600 hover:text-teal-700 hover:bg-teal-50">
                              <Check size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingStore(null)} className="text-slate-400 hover:text-slate-600">
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1">
                            <Button
                              data-testid={`edit-store-${sc.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingStore({ id: sc.id, store_name: sc.store_name, area_name: sc.area_name, assigned_3pa_email: sc.assigned_3pa_email })}
                              className="text-slate-400 hover:text-teal-600 hover:bg-teal-50"
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              data-testid={`delete-config-${sc.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteStoreConfig(sc.id)}
                              className="text-red-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Areas Tab */}
        <TabsContent value="areas" className="mt-4 space-y-4">
          <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Add New Area</p>
              <p className="text-xs text-slate-400 mt-1">SLA is counted in working days (Mon–Fri) from JC creation.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Area Name</Label>
                <Input
                  data-testid="area-name-input"
                  value={areaForm.area_name}
                  onChange={(e) => setAreaForm({ ...areaForm, area_name: e.target.value })}
                  placeholder="e.g. Bloemfontein"
                  className="bg-white border-slate-300"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Primary Collector (3PA)</Label>
                <Select value={areaForm.assigned_collector} onValueChange={(v) => setAreaForm({ ...areaForm, assigned_collector: v })}>
                  <SelectTrigger data-testid="area-collector-select" className="bg-white border-slate-300">
                    <SelectValue placeholder="Select 3PA user" />
                  </SelectTrigger>
                  <SelectContent>
                    {collectors.map((c) => (
                      <SelectItem key={c.email} value={c.email}>
                        {c.name} {c.company_name ? `· ${c.company_name}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">SLA (working days)</Label>
                <Input
                  data-testid="area-sla-input"
                  type="number"
                  min={1}
                  max={60}
                  value={areaForm.sla_days}
                  onChange={(e) => setAreaForm({ ...areaForm, sla_days: e.target.value })}
                  placeholder="5"
                  className="bg-white border-slate-300"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Coverage</Label>
                <Select value={areaForm.coverage} onValueChange={(v) => setAreaForm({ ...areaForm, coverage: v })}>
                  <SelectTrigger data-testid="area-coverage-select" className="bg-white border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOCAL">Local</SelectItem>
                    <SelectItem value="NATIONAL">National</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Region</Label>
                <Select value={areaForm.region || "__none__"} onValueChange={(v) => setAreaForm({ ...areaForm, region: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="area-region-select" className="bg-white border-slate-300">
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {regions.map((r) => {
                      const rn = typeof r === "string" ? r : r.name;
                      return (<SelectItem key={rn} value={rn}>{rn}</SelectItem>);
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              data-testid="add-area-btn"
              onClick={addArea}
              disabled={!areaForm.area_name.trim() || !areaForm.assigned_collector}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Plus size={14} className="mr-1" /> Add Area
            </Button>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                  <th className="text-left p-3">Area Name</th>
                  <th className="text-left p-3">Primary Collector</th>
                  <th className="text-left p-3">3PA Company</th>
                  <th className="text-left p-3">SLA (days)</th>
                  <th className="text-left p-3">Coverage</th>
                  <th className="text-left p-3">Region</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {areas.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-slate-400">No areas configured</td></tr>
                )}
                {areas.map((a) => {
                  const isEditing = editingArea?.id === a.id;
                  return (
                    <tr key={a.id || a.area_name} data-testid={`area-row-${a.id || a.area_name}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 font-medium text-slate-800">
                        {isEditing ? (
                          <Input
                            value={editingArea.area_name}
                            onChange={(e) => setEditingArea({ ...editingArea, area_name: e.target.value })}
                            className="h-8 bg-white border-slate-300 text-sm max-w-xs"
                          />
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <MapPin size={12} className="text-teal-600" />{a.area_name}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-slate-600">
                        {isEditing ? (
                          <Select value={editingArea.assigned_collector} onValueChange={(v) => setEditingArea({ ...editingArea, assigned_collector: v })}>
                            <SelectTrigger className="h-8 bg-white border-slate-300 text-xs w-64"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {collectors.map((c) => (<SelectItem key={c.email} value={c.email}>{c.name} ({c.email})</SelectItem>))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs">
                            {(collectors.find((c) => c.email === a.assigned_collector)?.name) || a.assigned_collector || "—"}
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        {a.company_name ? (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">{a.company_name}</Badge>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        {isEditing ? (
                          <Input
                            data-testid={`edit-area-sla-${a.id}`}
                            type="number"
                            min={1}
                            max={60}
                            value={editingArea.sla_days}
                            onChange={(e) => setEditingArea({ ...editingArea, sla_days: e.target.value })}
                            className="h-8 bg-white border-slate-300 text-sm w-20"
                          />
                        ) : (
                          <span className="text-slate-700 font-mono text-xs">{a.sla_days || 5}</span>
                        )}
                      </td>
                      <td className="p-3">
                        {isEditing ? (
                          <Select value={editingArea.coverage || "NATIONAL"} onValueChange={(v) => setEditingArea({ ...editingArea, coverage: v })}>
                            <SelectTrigger data-testid={`edit-area-coverage-${a.id}`} className="h-8 bg-white border-slate-300 text-xs w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="LOCAL">Local</SelectItem>
                              <SelectItem value="NATIONAL">National</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${
                            (a.coverage || "NATIONAL") === "LOCAL"
                              ? "bg-sky-50 text-sky-700 border-sky-200"
                              : "bg-violet-50 text-violet-700 border-violet-200"
                          }`}>
                            {a.coverage || "NATIONAL"}
                          </Badge>
                        )}
                      </td>
                      <td className="p-3">
                        {isEditing ? (
                          <Select value={editingArea.region || "__none__"} onValueChange={(v) => setEditingArea({ ...editingArea, region: v === "__none__" ? "" : v })}>
                            <SelectTrigger data-testid={`edit-area-region-${a.id}`} className="h-8 bg-white border-slate-300 text-xs w-40"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— None —</SelectItem>
                              {regions.map((r) => {
                                const rn = typeof r === "string" ? r : r.name;
                                return (<SelectItem key={rn} value={rn}>{rn}</SelectItem>);
                              })}
                            </SelectContent>
                          </Select>
                        ) : a.region ? (
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-700 border-amber-200">
                            {a.region}
                          </Badge>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {isEditing ? (
                          <div className="flex justify-end gap-1">
                            <Button data-testid={`save-area-${a.id}`} variant="ghost" size="sm" onClick={saveAreaEdit} className="text-teal-600 hover:text-teal-700 hover:bg-teal-50">
                              <Check size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingArea(null)} className="text-slate-400 hover:text-slate-600">
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1">
                            <Button
                              data-testid={`edit-area-${a.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingArea({ id: a.id, area_name: a.area_name, assigned_collector: a.assigned_collector, sla_days: a.sla_days || 5, coverage: a.coverage || "NATIONAL", region: a.region || "" })}
                              className="text-slate-400 hover:text-teal-600 hover:bg-teal-50"
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              data-testid={`delete-area-${a.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteArea(a)}
                              className="text-red-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Regions Tab */}
        <TabsContent value="regions" className="mt-4 space-y-4">
          <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Add New Region</p>
              <p className="text-xs text-slate-400 mt-1">Regions are used to group Areas (e.g. by province) and drive the dashboard's regional KPIs.</p>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1 max-w-sm space-y-1">
                <Label className="text-xs text-slate-500">Region Name</Label>
                <Input
                  data-testid="region-name-input"
                  value={regionForm}
                  onChange={(e) => setRegionForm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addRegion(); }}
                  placeholder="e.g. Cross-Border"
                  className="bg-white border-slate-300"
                />
              </div>
              <Button
                data-testid="add-region-btn"
                onClick={addRegion}
                disabled={!regionForm.trim()}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Plus size={14} className="mr-1" /> Add Region
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                  <th className="text-left p-3">Region</th>
                  <th className="text-left p-3">Linked Areas</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {regions.length === 0 && (
                  <tr><td colSpan={3} className="p-8 text-center text-slate-400">No regions configured</td></tr>
                )}
                {regions.map((r) => {
                  const isObj = typeof r === "object";
                  const id = isObj ? r.id : r;
                  const name = isObj ? r.name : r;
                  const inUse = isObj ? (r.in_use || 0) : 0;
                  const isEditing = editingRegion?.id === id;
                  return (
                    <tr key={id} data-testid={`region-row-${id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3">
                        {isEditing ? (
                          <Input
                            data-testid={`edit-region-input-${id}`}
                            value={editingRegion.name}
                            onChange={(e) => setEditingRegion({ ...editingRegion, name: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") saveRegionEdit(); if (e.key === "Escape") setEditingRegion(null); }}
                            autoFocus
                            className="h-8 bg-white border-slate-300 text-sm max-w-xs"
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <MapPin size={14} className="text-amber-600" />
                            <span className="font-medium text-slate-800">{name}</span>
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className={`text-xs ${inUse > 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                          {inUse} {inUse === 1 ? "area" : "areas"}
                        </Badge>
                      </td>
                      <td className="p-3 text-right">
                        {isEditing ? (
                          <div className="flex justify-end gap-1">
                            <Button data-testid={`save-region-${id}`} variant="ghost" size="sm" onClick={saveRegionEdit} className="text-teal-600 hover:text-teal-700 hover:bg-teal-50">
                              <Check size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingRegion(null)} className="text-slate-400 hover:text-slate-600">
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1">
                            <Button
                              data-testid={`edit-region-${id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingRegion({ id, name })}
                              className="text-slate-400 hover:text-teal-600 hover:bg-teal-50"
                              disabled={!isObj}
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              data-testid={`delete-region-${id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteRegion(isObj ? r : { id, name, in_use: 0 })}
                              disabled={inUse > 0}
                              title={inUse > 0 ? "Region is in use — reassign linked areas first" : "Delete"}
                              className={inUse > 0 ? "text-slate-300 cursor-not-allowed" : "text-red-400 hover:text-red-600 hover:bg-red-50"}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Companies Tab */}
        <TabsContent value="companies" className="mt-4 space-y-4">
          <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Add New 3rd-Party Company</p>
                <p className="text-xs text-slate-400 mt-1">Skyworth and CargoDash are built-in and cannot be renamed or deleted.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Company Name *</Label>
                <Input
                  data-testid="company-name-input"
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                  placeholder="e.g. Acme Logistics"
                  className="bg-white border-slate-300"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Contact Email</Label>
                <Input
                  data-testid="company-email-input"
                  value={companyForm.contact_email}
                  onChange={(e) => setCompanyForm({ ...companyForm, contact_email: e.target.value })}
                  placeholder="ops@company.co"
                  className="bg-white border-slate-300"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Contact Phone</Label>
                <Input
                  data-testid="company-phone-input"
                  value={companyForm.contact_phone}
                  onChange={(e) => setCompanyForm({ ...companyForm, contact_phone: e.target.value })}
                  placeholder="+27 11 123 4567"
                  className="bg-white border-slate-300"
                />
              </div>
            </div>
            <Button
              data-testid="add-company-btn"
              onClick={addCompany}
              disabled={!companyForm.name.trim()}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Plus size={14} className="mr-1" /> Add Company
            </Button>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                  <th className="text-left p-3">Company</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Contact Email</th>
                  <th className="text-left p-3">Contact Phone</th>
                  <th className="text-left p-3">Users</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-slate-400">No companies defined</td></tr>
                )}
                {companies.map((c) => {
                  const isEditing = editingCompany?.id === c.id;
                  const typeMeta = TYPE_META[c.type] || TYPE_META.THIRD_PARTY;
                  return (
                    <tr key={c.id} data-testid={`company-row-${c.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3">
                        {isEditing && !c.is_builtin ? (
                          <Input
                            data-testid={`edit-company-name-${c.id}`}
                            value={editingCompany.name}
                            onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })}
                            className="h-8 bg-white border-slate-300 text-sm"
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <Building2 size={14} className="text-teal-600" />
                            <span className="font-medium text-slate-800">{c.name}</span>
                            {c.is_builtin && (
                              <Badge variant="outline" className="text-[9px] uppercase tracking-wider bg-slate-50 text-slate-500 border-slate-200">
                                built-in
                              </Badge>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${typeMeta.cls}`}>
                          {typeMeta.label}
                        </Badge>
                      </td>
                      <td className="p-3 text-slate-600">
                        {isEditing ? (
                          <Input
                            value={editingCompany.contact_email || ""}
                            onChange={(e) => setEditingCompany({ ...editingCompany, contact_email: e.target.value })}
                            className="h-8 bg-white border-slate-300 text-sm"
                          />
                        ) : (c.contact_email || "—")}
                      </td>
                      <td className="p-3 text-slate-600">
                        {isEditing ? (
                          <Input
                            value={editingCompany.contact_phone || ""}
                            onChange={(e) => setEditingCompany({ ...editingCompany, contact_phone: e.target.value })}
                            className="h-8 bg-white border-slate-300 text-sm"
                          />
                        ) : (c.contact_phone || "—")}
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                          {c.user_count || 0} {c.user_count === 1 ? "user" : "users"}
                        </Badge>
                      </td>
                      <td className="p-3 text-right">
                        {isEditing ? (
                          <div className="flex justify-end gap-1">
                            <Button data-testid={`save-company-${c.id}`} variant="ghost" size="sm" onClick={saveCompanyEdit} className="text-teal-600 hover:text-teal-700 hover:bg-teal-50">
                              <Check size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingCompany(null)} className="text-slate-400 hover:text-slate-600">
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1">
                            <Button
                              data-testid={`edit-company-${c.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingCompany({ id: c.id, name: c.name, contact_email: c.contact_email || "", contact_phone: c.contact_phone || "", is_builtin: c.is_builtin })}
                              className="text-slate-400 hover:text-teal-600 hover:bg-teal-50"
                              title={c.is_builtin ? "Edit contact details (name is locked)" : "Edit"}
                            >
                              <Pencil size={14} />
                            </Button>
                            {c.is_builtin ? (
                              <Button
                                data-testid={`delete-company-${c.id}`}
                                variant="ghost"
                                size="sm"
                                disabled
                                aria-disabled="true"
                                className="text-slate-300 cursor-not-allowed"
                                title="Built-in companies cannot be deleted"
                              >
                                <Trash2 size={14} />
                              </Button>
                            ) : (
                              <Button
                                data-testid={`delete-company-${c.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteCompany(c)}
                                className="text-red-400 hover:text-red-600 hover:bg-red-50"
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
